import React, { useState, useEffect } from 'react';
import { Plus, Megaphone, Filter, Search } from 'lucide-react';
import AdsRequestForm from './AdsRequestForm';
import AdsRequestList from './AdsRequestList';
import AdsRequestQuickActionModal from './AdsRequestQuickActionModal';
import { useNotifications } from '../contexts/NotificationContext';
import { loadAdsRequests, deleteAdsRequest, updateAdsRequest } from '../api';

const AdsRequestSection = ({ currentUser }) => {
  const { showNotification } = useNotifications();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [tipoSolicitud, setTipoSolicitud] = useState('nueva');
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterPrioridad, setFilterPrioridad] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, searchTerm, filterEstado, filterTipo, filterPrioridad]);

  const fetchRequests = async () => {
    try {
      const data = await loadAdsRequests(currentUser?.email, currentUser?.role);
      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      showNotification('Error al cargar las solicitudes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(request =>
        request.nombre_cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.agente_asignado?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de estado
    if (filterEstado !== 'all') {
      filtered = filtered.filter(request => request.estado === filterEstado);
    }

    // Filtro de tipo
    if (filterTipo !== 'all') {
      filtered = filtered.filter(request => request.tipo_solicitud === filterTipo);
    }

    // Filtro de prioridad
    if (filterPrioridad !== 'all') {
      filtered = filtered.filter(request => request.prioridad === filterPrioridad);
    }

    // Ordenar por fecha (más recientes primero)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredRequests(filtered);
  };

  const handleCreateNueva = () => {
    setSelectedRequest(null);
    setTipoSolicitud('nueva');
    setShowForm(true);
  };

  const handleCreateActual = () => {
    setSelectedRequest(null);
    setTipoSolicitud('actual');
    setShowForm(true);
  };

  const handleEdit = (request) => {
    setSelectedRequest(request);
    setTipoSolicitud(request.tipo_solicitud);
    setShowForm(true);
  };

  const handleDelete = async (request) => {
    if (!window.confirm(`¿Estás seguro de eliminar la solicitud para "${request.nombre_cliente}"?`)) {
      return;
    }

    try {
      await deleteAdsRequest(request.id);
      showNotification('Solicitud eliminada exitosamente', 'success');
      fetchRequests();
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error al eliminar la solicitud', 'error');
    }
  };

  const handleComplete = async (request) => {
    setSelectedRequest(request);
    setShowQuickAction(true);
  };

  const handleFormSuccess = () => {
    fetchRequests();
  };

  const getCountByStatus = (status) => {
    if (status === 'all') return requests.length;
    return requests.filter(r => r.estado === status).length;
  };

  const getCountByType = (type) => {
    if (type === 'all') return requests.length;
    return requests.filter(r => r.tipo_solicitud === type).length;
  };

  const getProgressPercentage = () => {
    if (requests.length === 0) return 0;
    const completed = requests.filter(r => r.estado === 'completado' || r.estado === 'aprobado').length;
    return Math.round((completed / requests.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Título y descripción */}
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Megaphone className="text-blue-600" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Solicitudes de Ads</h1>
              <p className="text-sm text-gray-500">
                Gestiona las solicitudes de campañas de Google Ads y Meta Ads
              </p>
            </div>
          </div>

          {/* Indicador de progreso y botones */}
          <div className="flex items-center gap-4">
            <div className="bg-blue-50 px-6 py-3 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <div className="text-2xl font-bold text-blue-600">{getProgressPercentage()}%</div>
                  <div className="text-xs text-blue-600 font-medium">Avance</div>
                </div>
              </div>
            </div>

            {/* Botones de creación */}
            <button
              onClick={handleCreateNueva}
              className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              <Plus size={20} />
              <span>Nueva Campaña</span>
            </button>
            <button
              onClick={handleCreateActual}
              className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              <Plus size={20} />
              <span>Campaña Actual</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Búsqueda */}
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por cliente, descripción o agente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filtro de Estado */}
          <div>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los estados ({getCountByStatus('all')})</option>
              <option value="pendiente">Pendiente ({getCountByStatus('pendiente')})</option>
              <option value="en_proceso">En Proceso ({getCountByStatus('en_proceso')})</option>
              <option value="aprobado">Aprobado ({getCountByStatus('aprobado')})</option>
              <option value="rechazado">Rechazado ({getCountByStatus('rechazado')})</option>
              <option value="completado">Completado ({getCountByStatus('completado')})</option>
            </select>
          </div>

          {/* Filtro de Tipo */}
          <div>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los tipos ({getCountByType('all')})</option>
              <option value="nueva">Nueva ({getCountByType('nueva')})</option>
              <option value="actual">Actual ({getCountByType('actual')})</option>
            </select>
          </div>
        </div>

        {/* Contador de resultados */}
        <div className="mt-3 text-sm text-gray-600">
          Mostrando {filteredRequests.length} de {requests.length} solicitudes
        </div>
      </div>

      {/* Lista de Solicitudes */}
      <AdsRequestList
        requests={filteredRequests}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onComplete={handleComplete}
        currentUser={currentUser}
      />

      {/* Modales */}
      {showForm && (
        <AdsRequestForm
          request={selectedRequest}
          onClose={() => {
            setShowForm(false);
            setSelectedRequest(null);
          }}
          onSuccess={handleFormSuccess}
          currentUser={currentUser}
          tipoSolicitud={tipoSolicitud}
        />
      )}

      {showQuickAction && (
        <AdsRequestQuickActionModal
          request={selectedRequest}
          onClose={() => {
            setShowQuickAction(false);
            setSelectedRequest(null);
          }}
          onSuccess={handleFormSuccess}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default AdsRequestSection;
